<template>
  <div ref="bg">
    <div class="d-flex flex-column pb-16 mb-6" style="gap: 1rem">
      <!-- Capa e detalhes -->
      <div>
        <party-cover-video :party="data?.party" />

        <!-- Fast links -->
        <v-chip-group column>
          <v-chip
            v-for="fastLink in fastLinks"
            :key="fastLink.to"
            small
            outlined
            @click="goTo(fastLink)"
          >
            <v-icon v-if="fastLink.icon" left x-small>
              {{ fastLink.icon }}
            </v-icon>

            {{ fastLink.label }}
          </v-chip>
        </v-chip-group>
      </div>

      <banner-viewer
        v-if="banners.before_tickets"
        :banner="banners.before_tickets"
        :aspect-ratio="8 / 1"
        :image-url="banners.before_tickets.imageUrl"
        :link="banners.before_tickets.link"
        :isSafetyLink="banners.before_tickets.isSafetyLink"
      />

      <v-card elevation="0" rounded="lg" outlined>
        <v-card-title class="d-block">
          <div>
            <v-chip
              v-if="!loading"
              label
              small
              @click="goTo({ to: 'policies' })"
              :color="data?.party.ageGroup == 0 ? 'success' : 'info'"
              class="float-right ml-2 mt-1"
            >
              {{
                data?.party.ageGroup == 0 ? "Livre" : `+${data?.party.ageGroup}`
              }}
              {{ data?.party.ageWithParent !== -1 ? "*" : "" }}
            </v-chip>
            <h3
              class="mb-0 flex-grow-1"
              style="word-break: normal; line-height: 1.2"
            >
              <v-skeleton-loader v-if="loading" type="heading" height="30" />
              <span v-else>{{ data?.party.name }}</span>
            </h3>
            <h6
              v-if="loading || data?.party?.subtitle"
              class="mb-0 flex-grow-1 text--secondary"
              style="word-break: normal; line-height: 1.2"
            >
              <v-skeleton-loader
                v-if="loading"
                type="heading"
                height="17"
                width="60%"
              />
              <span v-else>{{ data?.party.subtitle }}</span>
            </h6>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="data?.party?.date || loading"
            class="d-flex flex-column gap-1 mb-2"
          >
            <div class="d-flex align-center">
              <v-icon small left>mdi-calendar-start</v-icon>
              <v-skeleton-loader
                class="flex-grow-1 mb-0 py-1"
                v-if="loading"
                type="heading"
                height="22"
              />
              <span v-else>{{ data?.party.date | formatDate }} até</span>
            </div>
            <div
              v-if="data?.party?.endDate || loading"
              class="d-flex align-center"
            >
              <v-icon small left>mdi-calendar-end</v-icon>
              <v-skeleton-loader
                class="flex-grow-1 mb-0 py-1"
                v-if="loading"
                type="heading"
                height="22"
              />
              <span v-else>{{ data?.party.endDate | formatDate }}</span>
            </div>
          </div>
          <div v-if="data?.party?.description">
            <h6 class="mb-0">Descrição</h6>
            <party-description-card :party="data?.party" />
          </div>
        </v-card-text>
      </v-card>

      <v-card
        v-if="data?.party?.ParentParty"
        :to="{
          name: 'shop.party',
          params: {
            orgSlug: data?.organization?.slug,
            partyId:
              data?.party?.ParentParty?.slug || data?.party?.ParentParty?.id,
          },
        }"
        class="rounded-lg"
        elevation="0"
        outlined
      >
        <div class="d-flex align-center gap-3">
          <div>
            <v-img
              :src="data?.party?.ParentParty?.cover"
              :alt="data?.party?.ParentParty?.name"
              height="60px"
              :width="(60 * 16) / 9"
              :aspect-ratio="16 / 9"
              class="rounded-l-lg"
            />
          </div>
          <div class="flex-grow-1">
            <p class="mb-0 lh-1 text-12">Esse evento faz parte de</p>
            <h6 class="mb-0 lh-1 text-14">
              {{ data?.party?.ParentParty?.name }}
            </h6>
          </div>
          <v-icon>mdi-chevron-right</v-icon>
        </div>
      </v-card>

      <template v-if="status === true && !loading">
        <my-ticket
          v-if="data?.tickets && data?.tickets.length > 0"
          :tickets="data?.tickets"
        />
        <template v-if="data?.ticketGroups">
          <ticket-group-selector
            :tickets="data?.tickets"
            :ticket-groups="data?.ticketGroups"
            :party="data?.party"
            :products="data?.products"
            :baseParty="data?.baseParty"
            :concession="data?.concession"
            :code="code"
            :periods="data?.periods"
            :fees="data?.fees"
            :tableMap="data?.tableMap"
            :seller="data?.seller"
            :coupon="data?.coupon"
            :ticket-office="data?.ticketOffice"
            :partyId="partyId"
            :orgSlug="orgSlug"
            :queueInfo="queueInfo"
            @refresh="$emit('refresh', false)"
            @changeParty="$emit('changeParty', $event)"
          />
          <party-sellers-list
            v-if="data?.sellers && data?.sellers.length"
            :party="data?.party"
            :sellers="data?.sellers"
            :loading="loading"
          />
        </template>
        <v-alert v-else color="warning" text class="mb-0" border="bottom">
          <template
            v-if="
              !!data?.party.maxTickets &&
              data?.tickets.length >= data?.party.maxTickets
            "
          >
            <b>
              Você possui a quantidade máxima de ingressos para esse evento.
            </b>
            {{ data?.party.maxTickets }} ingressos por pessoa.
          </template>
          <template v-else>
            <b>Você possui uma compra pendente.</b> Finalize-a para comprar mais
            ingressos.
          </template>
        </v-alert>
      </template>
      <v-alert
        v-else-if="!loading"
        text
        type="warning"
        class="mb-0"
        :icon="status == 'finished' ? 'mdi-calendar-remove' : undefined"
      >
        <b v-if="status == 'finished'">Este evento já terminou</b>
        <template v-else-if="status == 'cancelled'">
          <b>Esse evento foi cancelado.</b><br />
          {{ data?.party.cancelledReason }}
        </template>
        <b v-else>Esse evento está indisponível</b>
      </v-alert>

      <!-- Mapa do evento -->

      <banner-viewer
        v-if="banners.after_tickets"
        :banner="banners.after_tickets"
        :aspect-ratio="9 / 2"
        :image-url="banners.after_tickets.imageUrl"
        :link="banners.after_tickets.link"
        :isSafetyLink="banners.after_tickets.isSafetyLink"
      />
      <party-map v-if="data?.party?.imageMap" :data="data" />
      <PartyTableMap
        v-if="
          data?.tableMap &&
          data?.tableMap.showInEventPage &&
          status === true &&
          !loading
        "
        ref="tableMap"
        :table-map="data?.tableMap"
        :party="data?.party"
        :loading="loading"
        :ticket-groups="data?.ticketGroups"
      />

      <!-- Endereço -->
      <party-address ref="address" :party="data?.party" :loading="loading" />

      <!-- Organização -->
      <organization-card
        ref="organization"
        :organization="data?.organization"
        :loading="loading"
      />

      <banner-viewer
        v-if="banners.sidebar"
        :banner="banners.sidebar"
        :aspect-ratio="4 / 3"
        :image-url="banners.sidebar.imageUrl"
        :link="banners.sidebar.link"
        :isSafetyLink="banners.sidebar.isSafetyLink"
      />

      <!-- Spotify -->
      <spotify-embed v-if="data?.party.spotifyId" :id="data?.party.spotifyId" />

      <!-- Produtos -->
      <product-row-select
        v-if="data?.ticketGroups && data?.products.length"
        :products="data?.products"
        :party="data?.party"
        class="mt-4"
      />

      <!-- Mapa 3D -->
      <GLTFMapViewer
        v-if="has3DMap"
        ref="3dMap"
        :map="data?.map"
        :loading="loading"
      />

      <!-- Galleria -->
      <party-gallery
        ref="gallery"
        v-if="hasGallery"
        :gallery="data?.gallery"
        :loading="loading"
      />

      <!-- Atrações -->
      <attractions-list
        ref="attractions"
        v-if="hasAttractions"
        :attractions="data?.attractions"
        :periods="data?.periods"
        :tickets="data?.tickets"
        :loading="loading"
      />

      <!-- Políticas -->
      <party-policies ref="policies" :party="data?.party" :loading="loading" />

      <!-- FAQ -->
      <faq-viewer ref="faqs" v-if="hasFaq" :data="data?.faqs" viewOnly />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import PartyStatus from "@/utils/partyStatus";

import PartyDescriptionCard from "@/components/shop/PartyDescriptionCard.vue";
import PartyGallery from "@/components/shop/PartyGallery.vue";
import MyTicket from "@/components/shop/MyTicket.vue";
import TicketGroupSelector from "@/components/shop/TicketGroupSelector.vue";
import ProductRowSelect from "@/components/shop/ProductRowSelect.vue";
import AttractionsList from "@/components/global/party/AttractionsList.vue";
import FaqViewer from "../../components/admin/party/FAQViewer.vue";
import OrganizationCard from "@/components/shop/OrganizationCard.vue";
import PartyAddress from "../../components/shop/PartyAddress.vue";
import PartyMap from "../../components/shop/PartyMap.vue";
import PartyTableMap from "../../components/shop/PartyTableMap.vue";

import PartyPolicies from "../../components/shop/PartyPolicies.vue";
import GLTFMapViewer from "../../components/shop/GLTFMapViewer.vue";
import PartyCoverVideo from "../../components/shop/PartyCoverVideo.vue";
import PartySellersList from "../../components/shop/PartySellersList.vue";
import SpotifyEmbed from "../../components/global/SpotifyEmbed.vue";

import BannerViewer from "@/components/global/BannerViewer.vue";

export default {
  components: {
    PartyDescriptionCard,
    PartyGallery,
    MyTicket,
    TicketGroupSelector,
    ProductRowSelect,
    AttractionsList,
    FaqViewer,
    OrganizationCard,
    PartyMap,
    PartyAddress,
    PartyTableMap,
    PartyPolicies,
    GLTFMapViewer,
    PartyCoverVideo,
    PartySellersList,
    SpotifyEmbed,
    BannerViewer,
  },
  data() {
    return {
      fLinks: [
        {
          icon: "mdi-share-variant",
          label: "Compartilhar",
          func: this.share,
          if: "canShare",
        },
        {
          label: "Mapa",
          to: "tableMap",
          if: "hasTableMap",
        },
        {
          label: "Local",
          to: "address",
          if: "hasAddress",
        },
        {
          label: "Organização",
          to: "organization",
        },
        {
          label: "Mapa",
          to: "3dMap",
          if: "has3DMap",
        },
        {
          label: "Galeria",
          to: "gallery",
          if: "hasGallery",
        },
        {
          label: "Cronograma",
          to: "attractions",
          if: "hasAttractions",
        },
        {
          label: "Políticas",
          to: "policies",
        },
      ],
    };
  },
  methods: {
    share() {
      navigator.share({
        title: this.data?.party.name,
        text: this.data?.party.name,
        url: window.location.href,
      });
    },
    goTo({ to, func }) {
      if (func) return func();

      const el = this.$refs[to];
      this.$vuetify.goTo(el || to, {
        offset: 30,
        behavior: "smooth",
      });
    },
    setBg() {
      this.$root.$emit("setImgBg", this.data?.party.cover);
    },
  },
  mounted() {
    this.setBg();
  },
  destroyed() {
    this.$root.$emit("setImgBg", null);
  },
  watch: {
    "data.party.cover": {
      handler() {
        this.setBg();
      },
    },
  },
  computed: {
    status() {
      const party = this.data?.baseParty || this.data?.party;
      if (!party) return false;
      const status = new PartyStatus(party);
      return status.isActive || status.status;
    },
    canShare() {
      return navigator.share && this.isPublic;
    },
    isPublic() {
      return !this.data?.party?.private;
    },
    userHasTicket() {
      return this.data?.tickets && this.data?.tickets.length > 0;
    },
    hasGallery() {
      return this.data?.gallery && this.data?.gallery.length > 0;
    },
    hasFaq() {
      return this.data?.faqs && this.data?.faqs.length > 0;
    },
    hasAttractions() {
      return this.data?.attractions && this.data?.attractions.length > 0;
    },
    hasAddress() {
      return !!this.data?.party.Address;
    },
    hasTableMap() {
      return this.data?.tableMap;
    },
    has3DMap() {
      return this.data?.map;
    },
    fastLinks() {
      return this.fLinks.filter((link) => {
        if (link.if) return this[link.if];
        return true;
      });
    },
    banners() {
      const bannerMap = {
        before_tickets: null,
        after_tickets: null,
        sidebar: null,
      };

      if (this.data?.banners) {
        this.data.banners.forEach((banner) => {
          if (banner.position in bannerMap) {
            bannerMap[banner.position] = banner;
          }
        });
      }

      return bannerMap;
    },
  },
  filters: {
    formatDate(date) {
      const momentDate = moment(date);
      var formatPattern = "DD [de] MMMM";

      if (momentDate.year() != moment().year()) formatPattern += " [de] YYYY";
      else if (momentDate.isBefore(moment(), "day"))
        formatPattern += " [de] YYYY";

      formatPattern += " [às] HH[h]mm";

      return momentDate.format(formatPattern);
    },
  },
  props: {
    data: {
      type: Object,
    },
    code: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    partyId: {
      type: String,
      required: true,
    },
    orgSlug: {
      type: String,
      required: true,
    },
    queueInfo: {
      type: Object,
    },
  },
};
</script>

<style></style>
